/**
 * Selection state for entity picker rows (CanvasInsertMenu).
 * Mirrors the Cmd/Shift/range mechanics of the canvas explorer's
 * WorkspaceScope, scoped to a single popover instance
 * so selections don't bleed across picker views or across popovers.
 *
 * An internal entity registry (id → Entity) lets each mounted
 * `EntityDragRow` register its object so the bag can build a correct
 * multi-entity drag payload without needing a flat list passed in from
 * the parent.
 *
 * Consumers:
 *   - `EntitySelectionProvider` — mounts one bag per popover instance.
 *   - `useEntitySelection` — reads the bag from context; used by
 *     `EntityDragRow` to apply visual state and build the drag snapshot.
 */

import type { Entity } from "@cohesive/ui-components";
import { useDndMonitor } from "@dnd-kit/core";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export interface EntitySelectionValue {
  selectedIds: Set<string>;
  isSelected: (id: string) => boolean;
  /** Plain click: clear, add just this id (does NOT call onSelect). */
  selectOnly: (id: string) => void;
  /** Cmd/Ctrl click: toggle a single id. */
  toggle: (id: string) => void;
  /** Shift click: range-select from anchor to `id` in `orderedIds`. */
  rangeSelect: (orderedIds: string[], id: string) => void;
  clear: () => void;
  /**
   * Register an entity object so the bag can build multi-entity drag
   * payloads. Called by each mounted `EntityDragRow`; cleaned up on unmount.
   */
  registerEntity: (id: string, entity: Entity) => void;
  unregisterEntity: (id: string) => void;
  /**
   * Returns the Entity objects for all currently selected ids. Falls back
   * to `fallback` for ids that were never registered (e.g. the row is
   * not currently mounted because it's scrolled out of view).
   */
  getSelectedEntities: (fallback: Entity) => Entity[];
}

const EntitySelectionContext = createContext<EntitySelectionValue | null>(null);

export function EntitySelectionProvider({ children }: { children: ReactNode }) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const anchorIdRef = useRef<string | null>(null);
  // Ref (not state) — the registry changes don't need to trigger re-renders.
  const entityRegistryRef = useRef<Map<string, Entity>>(new Map());
  // Mirror of selectedIds as a ref so unregisterEntity can check it
  // without needing selectedIds in its dependency array (keeping it stable).
  const selectedIdsRef = useRef<Set<string>>(selectedIds);
  selectedIdsRef.current = selectedIds;

  const registerEntity = useCallback((id: string, entity: Entity) => {
    entityRegistryRef.current.set(id, entity);
  }, []);

  const unregisterEntity = useCallback((id: string) => {
    // Keep selected entities in the registry even after their row unmounts
    // (e.g. scrolled out of view in a virtualized list). Without this,
    // multi-select drags across a scroll boundary silently drop the
    // off-screen entities from the payload.
    if (!selectedIdsRef.current.has(id)) {
      entityRegistryRef.current.delete(id);
    }
  }, []);

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds],
  );

  const selectOnly = useCallback((id: string) => {
    anchorIdRef.current = id;
    setSelectedIds(new Set([id]));
  }, []);

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        if (anchorIdRef.current === id) anchorIdRef.current = null;
      } else {
        next.add(id);
        anchorIdRef.current = id;
      }
      return next;
    });
  }, []);

  const rangeSelect = useCallback((orderedIds: string[], targetId: string) => {
    const anchor = anchorIdRef.current;
    if (!anchor) {
      anchorIdRef.current = targetId;
      setSelectedIds(new Set([targetId]));
      return;
    }
    const anchorIdx = orderedIds.indexOf(anchor);
    const targetIdx = orderedIds.indexOf(targetId);
    if (anchorIdx === -1 || targetIdx === -1) {
      // Anchor is no longer in the visible list (e.g. filter/search changed).
      // Treat targetId as the new anchor so the next Shift+click can
      // produce a real range instead of hitting this fallback again.
      anchorIdRef.current = targetId;
      setSelectedIds(new Set([targetId]));
      return;
    }
    const start = Math.min(anchorIdx, targetIdx);
    const end = Math.max(anchorIdx, targetIdx);
    setSelectedIds(new Set(orderedIds.slice(start, end + 1)));
  }, []);

  const clear = useCallback(() => {
    anchorIdRef.current = null;
    // Purge retained-but-unmounted entries so the registry doesn't grow
    // unboundedly across successive selections.
    for (const id of selectedIdsRef.current) {
      if (!entityRegistryRef.current.has(id)) continue;
      // Only remove entries whose rows are currently unmounted (mounted
      // rows will re-register themselves on the next render cycle).
      entityRegistryRef.current.delete(id);
    }
    setSelectedIds(new Set());
  }, []);

  const getSelectedEntities = useCallback(
    (fallback: Entity): Entity[] => {
      const result: Entity[] = [];
      for (const id of selectedIds) {
        const entity = entityRegistryRef.current.get(id);
        if (entity) {
          result.push(entity);
        }
      }
      // If nothing resolved (e.g. called outside a selection), include fallback.
      return result.length > 0 ? result : [fallback];
    },
    [selectedIds],
  );

  // Clear selection when a dnd-kit drag finishes (successful drop or
  // cancelled). The provider renders inside CanvasDndProvider's DndContext
  // via Popover.Portal, so useDndMonitor is always in scope.
  useDndMonitor({ onDragEnd: clear });

  // Esc clears the selection while the popover is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") clear();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [clear]);

  const value = useMemo<EntitySelectionValue>(
    () => ({
      selectedIds,
      isSelected,
      selectOnly,
      toggle,
      rangeSelect,
      clear,
      registerEntity,
      unregisterEntity,
      getSelectedEntities,
    }),
    [
      selectedIds,
      isSelected,
      selectOnly,
      toggle,
      rangeSelect,
      clear,
      registerEntity,
      unregisterEntity,
      getSelectedEntities,
    ],
  );

  return (
    <EntitySelectionContext.Provider value={value}>
      {children}
    </EntitySelectionContext.Provider>
  );
}

/**
 * Returns the entity selection bag from the nearest
 * `EntitySelectionProvider`. Returns a no-op bag when called outside a
 * provider so plain entity pickers keep working without changes.
 */
export function useEntitySelection(): EntitySelectionValue {
  const ctx = useContext(EntitySelectionContext);
  return ctx ?? NOOP_SELECTION;
}

const NOOP_SELECTION: EntitySelectionValue = {
  selectedIds: new Set(),
  isSelected: () => false,
  selectOnly: () => {},
  toggle: () => {},
  rangeSelect: () => {},
  clear: () => {},
  registerEntity: () => {},
  unregisterEntity: () => {},
  getSelectedEntities: (fallback) => [fallback],
};
